import { isValidNum } from "../mathUtils.js";

export const EUI_STRING_LENGTH = 16;

export const SHORT_CODE_FORMAT = "CVC-NNN";

export const consonants = [
    "B",
    "C",
    "D",
    "F",
    "G",
    "J",
    "K",
    "L",
    "M",
    "N",
    "P",
    "S",
    "T",
    "V",
    "Z",
    "H",
    "R",
    "W",
    "Y",
];

/**
 * Consonants with sounds that are not easy to mistake for others.
 * Prefer drawing from this list to drawing from the "all consonants" list.
 */
export const strongConsonants = ["B", "C", "D", "F", "G", "L", "M", "N", "P", "R", "S", "T", "V", "Z"];

export const vowels = ["A", "E", "I", "O", "U"];
export const rudeWords = [
    "GAY",
    "JIZ",
    "CUM",
    "FAG",
    "GOD",
    "GIT",
    "JEW",
    "COK",
    "FUK",
    "FUX",
    "TIT",
    "NIG",
    "VAG",
    "JAP",
    "666",
];

// Not added to rudeWords: that would change how existing legacy numeric short codes decode.
export const verdiIdRudeWords = [...rudeWords, "NIP", "GIZ", "RIM", "VUL", "BAG"];

export const cvc = `(?:${consonants.join("|")})(?:${vowels.join("|")})(?:${consonants.join("|")})`;
export const verdiIdRegex = new RegExp(`^[A-Z]-${cvc}-\\d{3,4}$`);

export const rudeWordReplacements = {
    GAY: "GUY",
    JIZ: "JAZ",
    CUM: "CAM",
    FAG: "FIG",
    GOD: "GID",
    GIT: "GET",
    JEW: "JAW",
    COK: "CIK",
    FUK: "DUK",
    FUX: "FIX",
    TIT: "TAT",
    NIG: "NAG",
    VAG: "VIG",
    JAP: "JIP",
    "666": "665",
};
export const randomChar = [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
];
export const alphaNumeric = [
    "a",
    "A",
    "b",
    "B",
    "C",
    "d",
    "D",
    "e",
    "E",
    "f",
    "F",
    "g",
    "G",
    "h",
    "H",
    "i",
    "I",
    "j",
    "J",
    "K",
    "L",
    "m",
    "M",
    "n",
    "N",
    "p",
    "P",
    "q",
    "Q",
    "r",
    "R",
    "S",
    "t",
    "T",
    "u",
    "U",
    "v",
    "V",
    "W",
    "X",
    "y",
    "Y",
    "Z",
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
];

/**
 * Get the number of options that exist (or the effective base) of a particular digit based on the
 * format character provided.
 *
 * Defaults to 1 since that allows non-encoding characters to not contribute to the value.
 *
 * @param formatChar - The format character (C, V, L, N, etc.) to get the character set size for
 * @returns The number of possible values for the given format character
 */
export function getLengthOfCharacterFormat(formatChar: string) {
    return (
        {
            C: consonants.length,
            V: vowels.length,
            L: 25,
            N: 10,
            "*": randomChar.length,
            A: alphaNumeric.length,
        }[formatChar.toUpperCase()] ?? 1
    );
}

/**
 * Gets the actual value of a digit based on its format character. This is the value before considering
 * its digit location, just the value of the single digit itself.
 *
 * @param formatChar - The format character (C, V, L, N, etc.) that determines the character set
 * @param char - The character to get the value for
 * @returns The numeric value of the character, or -1 if it is not a valid digit
 */
export function getValueOfEncodedCharacter(formatChar: string, char: string): number {
    formatChar = formatChar.toUpperCase();

    if (formatChar === "C") {
        return consonants.indexOf(char.toUpperCase());
    }

    if (formatChar === "V") {
        return vowels.indexOf(char.toUpperCase());
    }

    if (formatChar === "L") {
        return randomChar.indexOf(char.toLowerCase());
    }

    if (formatChar === "N") {
        const parsedInt = parseInt(char, 10);
        if (isValidNum(parsedInt)) {
            return parsedInt;
        }
        return -1;
    }

    if (formatChar === "*") {
        return randomChar.indexOf(char.toLowerCase());
    }

    if (formatChar === "A") {
        return alphaNumeric.indexOf(char);
    }

    // if it's a non-encoding character, it can just return 0
    if (formatChar === char) {
        return 0;
    }
    return -1;
}
